import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import Loader from 'components/loaderComponent';

import { useGetPage } from 'hooks/useGetPage';
import {
  GOOGLE_EHR_AVAILABLE_SYSTEMS_IDENTIFIERS,
  pageIds,
} from 'utilities/constants';
import {
  FhirQuestionnaireNames,
  useGetFullscriptAllergensByPatientHealthQuestionnaireQuery,
} from '../../../../graphql/generated/remote-schema-hasura';
import { useAuth } from '../../../../auth/context/AuthContext';
import { Roles } from '../../../../firebase/interfaces';
import { interpolateVariables } from '../../../../utilities/functions';

interface AllergensBlockProps {
  patientName: string;
  patientCodexId: string;
}

export const AllergensBlock: React.FC<AllergensBlockProps> = ({
  patientCodexId,
  patientName,
}) => {
  const { patientId: patientIdFromUrl } = useParams();
  const { user } = useAuth();

  const patientId = patientCodexId || patientIdFromUrl;

  const { loading: localeLoading, data: locale } = useGetPage({
    pageId: pageIds.ALLERGENS_TAB,
    locale: 'en',
  });

  const isPatient = user?.role === Roles.PATIENT;

  const [allergens, setAllergens] = useState<string[]>([]);

  const { loading } =
    useGetFullscriptAllergensByPatientHealthQuestionnaireQuery({
      variables: {
        codexPatientId: patientId as string,
        identifier: FhirQuestionnaireNames.HealthQuestionnaire,
        system: GOOGLE_EHR_AVAILABLE_SYSTEMS_IDENTIFIERS.codex,
      },
      onCompleted: (data) => {
        const allergenNames =
          data.getFullscriptAllergensFromQuestionnaireResponse.fullscript_allergens.map(
            (allergen) => {
              return allergen.fullscript_name;
            },
          );

        setAllergens(allergenNames);
      },
    });

  if (!allergens.length)
    return (
      <div className="flex w-full h-full gap-2.5 py-2.5">
        <p className="m-auto text-med-gray-3 font-medium">
          No dietary preferences or allergies selected
        </p>
      </div>
    );

  if (loading || localeLoading || !locale) {
    return <Loader />;
  }

  return (
    <div
      data-testid="questionnaire-tab"
      className="relative self-stretch desktop:min-h-[200px] bg-white rounded-10 gap-1"
    >
      <div className="flex flex-col">
        <div className="justify-center text-neutral-800 font-semibold">
          {isPatient
            ? locale.patientTitle
            : interpolateVariables(locale.providerTitle, {
                patientName: patientName,
              })}
        </div>
        <div className="text-neutral-800 font-semibold">
          {!isPatient && locale.providerSubtitle}
        </div>
      </div>

      <div className="text-neutral-800 mt">{allergens?.join(', ')}</div>
    </div>
  );
};

export default AllergensBlock;
